import { AuthorityName } from '@fides/fides-react-components';
import { SignatureStatus, SourceType } from 'core/enums';

type HasAuthorityName = (authorityName: AuthorityName) => boolean;

const isOpenUploadedPaymentNotDeleted = (
  sourceType?: SourceType | null,
  signatureStatus?: SignatureStatus | null,
  deleted?: boolean
): boolean =>
  !deleted &&
  signatureStatus === SignatureStatus.OPEN &&
  (sourceType === SourceType.UPLOAD || sourceType === SourceType.FILE_ENTRY);

const isOpenManualPaymentNotDeleted = (
  sourceType?: SourceType | null,
  signatureStatus?: SignatureStatus | null,
  deleted?: boolean
): boolean =>
  !deleted &&
  signatureStatus === SignatureStatus.OPEN &&
  (sourceType === SourceType.EFT_WEB || sourceType === SourceType.CPS_WEB);

export interface PaymentPermissions {
  canCreatePaymentsOrFromTemplate: boolean;
  canFullyEditUploadedPayments: boolean;
  canEditUploadedPaymentByPermissions: boolean;
  canEditManualPaymentByPermissions: boolean;
  canAccessAnyPaymentEditByPermissions: boolean;
  canAccessPaymentEditByTypeAndPermissions: (params: {
    sourceType?: SourceType | null;
    signatureStatus?: SignatureStatus | null;
    deleted?: boolean;
  }) => boolean;
  canAccessPaymentEditRouteByPayment: (params: {
    impersonated?: boolean;
    sourceType?: SourceType | null;
    signatureStatus?: SignatureStatus | null;
    deleted?: boolean;
  }) => boolean;
  canFullyEditPaymentByTypeAndPermissions: (params: {
    sourceType?: SourceType | null;
    signatureStatus?: SignatureStatus | null;
    deleted?: boolean;
  }) => boolean;
  canAccessFullPaymentEditRouteByPayment: (params: {
    impersonated?: boolean;
    sourceType?: SourceType | null;
    signatureStatus?: SignatureStatus | null;
    deleted?: boolean;
  }) => boolean;
}

export const computePaymentPermissions = (hasAuthorityName: HasAuthorityName): PaymentPermissions => {
  const hasCreateAny = hasAuthorityName('PAYMENT_CREATE_ANY_GRANT');
  const hasCreateFromTemplate = hasAuthorityName('PAYMENT_CREATE_FROM_TEMPLATE_GRANT');
  const hasEditFullUploaded = hasAuthorityName('PAYMENT_EDIT_FULL_UPLOADED_GRANT');
  const hasDeleteTransactionsUploaded = hasAuthorityName('PAYMENT_DELETE_TRANSACTIONS_UPLOADED_GRANT');
  const hasEditFull = hasAuthorityName('PAYMENT_EDIT_FULL_GRANT');

  const canCreatePaymentsOrFromTemplate = hasCreateAny || hasCreateFromTemplate;
  const canFullyEditUploadedPayments = canCreatePaymentsOrFromTemplate && hasEditFullUploaded;
  const canEditUploadedPaymentByPermissions = canFullyEditUploadedPayments || hasDeleteTransactionsUploaded;
  const canEditManualPaymentByPermissions = canCreatePaymentsOrFromTemplate && hasEditFull;
  const canAccessAnyPaymentEditByPermissions = canEditUploadedPaymentByPermissions || canEditManualPaymentByPermissions;

  const canAccessPaymentEditByTypeAndPermissions = ({
    sourceType,
    signatureStatus,
    deleted,
  }: {
    sourceType?: SourceType | null;
    signatureStatus?: SignatureStatus | null;
    deleted?: boolean;
  }): boolean =>
    (isOpenUploadedPaymentNotDeleted(sourceType, signatureStatus, deleted) && canEditUploadedPaymentByPermissions) ||
    (isOpenManualPaymentNotDeleted(sourceType, signatureStatus, deleted) && canEditManualPaymentByPermissions);

  const canAccessPaymentEditRouteByPayment = ({
    impersonated,
    sourceType,
    signatureStatus,
    deleted,
  }: {
    impersonated?: boolean;
    sourceType?: SourceType | null;
    signatureStatus?: SignatureStatus | null;
    deleted?: boolean;
  }): boolean => !impersonated && canAccessPaymentEditByTypeAndPermissions({ sourceType, signatureStatus, deleted });

  // uploaded or manual payments. Used where "edit access" must mean full editing, not delete-only.
  const canFullyEditPaymentByTypeAndPermissions = ({
    sourceType,
    signatureStatus,
    deleted,
  }: {
    sourceType?: SourceType | null;
    signatureStatus?: SignatureStatus | null;
    deleted?: boolean;
  }): boolean =>
    (isOpenUploadedPaymentNotDeleted(sourceType, signatureStatus, deleted) && canFullyEditUploadedPayments) ||
    (isOpenManualPaymentNotDeleted(sourceType, signatureStatus, deleted) && canEditManualPaymentByPermissions);

  const canAccessFullPaymentEditRouteByPayment = ({
    impersonated,
    sourceType,
    signatureStatus,
    deleted,
  }: {
    impersonated?: boolean;
    sourceType?: SourceType | null;
    signatureStatus?: SignatureStatus | null;
    deleted?: boolean;
  }): boolean => !impersonated && canFullyEditPaymentByTypeAndPermissions({ sourceType, signatureStatus, deleted });

  return {
    canCreatePaymentsOrFromTemplate,
    canFullyEditUploadedPayments,
    canEditUploadedPaymentByPermissions,
    canEditManualPaymentByPermissions,
    canAccessAnyPaymentEditByPermissions,
    canAccessPaymentEditByTypeAndPermissions,
    canAccessPaymentEditRouteByPayment,
    canFullyEditPaymentByTypeAndPermissions,
    canAccessFullPaymentEditRouteByPayment,
  };
};
